import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    Plus,
    Trash2,
    Upload,
    Download,
    Search,
    Pencil,
    TableProperties,
    Truck,
    X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useRBAC } from '@/hooks/useRBAC';
import { ApiError, rigs as rigsApi, type RigRow, type RigUpsertPayload } from '@/lib/api';

type RigStatus = 'active' | 'decommissioned' | 'maintenance';
type RigType = 'D65' | 'MD6250' | 'Other';

interface RigForm {
    id?: string;
    rigId: string;
    rigType: RigType;
    manufacturer: string;
    serialNumber: string;
    site: string;
    status: RigStatus;
    notes: string;
}

const TYPE_LABELS: Record<RigType, string> = {
    D65: 'D65',
    MD6250: 'MD6250',
    Other: 'Other',
};

const STATUS_LABELS: Record<RigStatus, string> = {
    active: 'Active',
    decommissioned: 'Decommissioned',
    maintenance: 'Maintenance',
};

const STATUS_COLORS: Record<RigStatus, string> = {
    active: 'bg-green-500/15 text-green-400 border-green-500/30',
    decommissioned: 'bg-red-500/15 text-red-400 border-red-500/30',
    maintenance: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
};

const MANUFACTURERS = ['Epiroc', 'Sandvik', 'Caterpillar', 'Komatsu', 'Atlas Copco', 'Other'];
const SITES = ['Wodgina', 'Tom Price', 'Newman'];

const EMPTY_FORM: RigForm = {
    rigId: '',
    rigType: 'D65',
    manufacturer: '',
    serialNumber: '',
    site: '',
    status: 'active',
    notes: '',
};

function rowToForm(r: RigRow): RigForm {
    return {
        id: r.id,
        rigId: r.rig_id,
        rigType: r.rig_type,
        manufacturer: r.manufacturer ?? '',
        serialNumber: r.serial_number ?? '',
        site: r.site_id ?? '',
        status: r.status,
        notes: r.notes,
    };
}

function formToPayload(f: RigForm): RigUpsertPayload {
    return {
        rig_id: f.rigId,
        rig_type: f.rigType,
        manufacturer: f.manufacturer || null,
        serial_number: f.serialNumber || null,
        site_id: f.site || null,
        status: f.status,
        notes: f.notes,
    };
}

/* ── CSV helpers ──────────────────────────────────────────────── */

const CSV_HEADERS = ['rigId', 'rigType', 'manufacturer', 'serialNumber', 'site', 'status', 'notes'];

function downloadRigTemplate() {
    const sample: RigForm[] = [
        {
            rigId: 'DR1001',
            rigType: 'D65',
            manufacturer: 'Epiroc',
            serialNumber: 'EP-D65-001',
            site: 'Wodgina',
            status: 'active',
            notes: '',
        },
        {
            rigId: 'DR2001',
            rigType: 'MD6250',
            manufacturer: 'Caterpillar',
            serialNumber: 'CAT-MD-771',
            site: 'Tom Price',
            status: 'active',
            notes: '',
        },
        {
            rigId: 'DR1002',
            rigType: 'D65',
            manufacturer: 'Epiroc',
            serialNumber: 'EP-D65-002',
            site: 'Newman',
            status: 'maintenance',
            notes: 'Awaiting service',
        },
    ];
    const rows = [
        CSV_HEADERS.join(','),
        ...sample.map((r) =>
            CSV_HEADERS.map((h) => {
                const v = String(r[h as keyof RigForm] ?? '');
                return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
            }).join(',')
        ),
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'rig-register-template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
}

function exportRigsCSV(rigs: RigForm[]) {
    const rows = [
        CSV_HEADERS.join(','),
        ...rigs.map((r) =>
            CSV_HEADERS.map((h) => {
                const v = String(r[h as keyof RigForm] ?? '');
                return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
            }).join(',')
        ),
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'rig-register.csv';
    a.click();
    URL.revokeObjectURL(a.href);
}

function parseCSVLine(line: string): string[] {
    const result: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
            if (!inQuotes && cur === '') {
                inQuotes = true;
            } else if (inQuotes && line[i + 1] === '"') {
                cur += '"';
                i++;
            } else if (inQuotes) {
                inQuotes = false;
            } else {
                cur += ch;
            }
        } else if (ch === ',' && !inQuotes) {
            result.push(cur.trim());
            cur = '';
        } else {
            cur += ch;
        }
    }
    result.push(cur.trim());
    return result;
}

const TYPE_NORMALISE: Record<string, RigType> = {
    d65: 'D65',
    md6250: 'MD6250',
    'md 6250': 'MD6250',
    'md-6250': 'MD6250',
    other: 'Other',
};

function parseRigsCSV(text: string): { entries: RigForm[]; errors: string[] } {
    const clean = text.replace(/^﻿/, '');
    const lines = clean.split(/\r?\n|\r/).filter((l) => l.trim());
    if (lines.length < 2) return { entries: [], errors: ['CSV has no data rows.'] };

    const headers = parseCSVLine(lines[0]).map((h) => h.toLowerCase().trim());
    const idx = (name: string) => headers.indexOf(name.toLowerCase());
    const entries: RigForm[] = [];
    const errors: string[] = [];

    for (let i = 1; i < lines.length; i++) {
        const cols = parseCSVLine(lines[i]);
        const get = (name: string) => (cols[idx(name)] ?? '').trim();

        const rigId = get('rigid') || get('rig_id') || get('rig id');
        if (!rigId) {
            errors.push(`Row ${i + 1}: missing rig ID`);
            continue;
        }

        const rawType = (get('rigtype') || get('rig_type') || get('type')).toLowerCase();
        const rigType = TYPE_NORMALISE[rawType];
        if (!rigType) {
            errors.push(`Row ${i + 1}: unrecognised rig type "${rawType || '(blank)'}"`);
            continue;
        }

        const rawStatus = get('status').toLowerCase();
        const status: RigStatus = (
            ['active', 'decommissioned', 'maintenance'].includes(rawStatus) ? rawStatus : 'active'
        ) as RigStatus;

        entries.push({
            rigId,
            rigType,
            manufacturer: get('manufacturer'),
            serialNumber: get('serialnumber') || get('serial_number') || get('serial'),
            site: get('site'),
            status,
            notes: get('notes'),
        });
    }
    return { entries, errors };
}

/* ── Page component ───────────────────────────────────────────── */

export default function RigRegister() {
    const { can } = useRBAC();
    const [rigs, setRigs] = useState<RigForm[]>([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState<RigForm>({ ...EMPTY_FORM });
    const [editingId, setEditingId] = useState<string | null>(null);
    const [showUpload, setShowUpload] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [importMessage, setImportMessage] = useState<{ text: string; error: boolean } | null>(
        null
    );
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Filters
    const [filterSite, setFilterSite] = useState<string>('all');
    const [filterType, setFilterType] = useState<string>('all');
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [searchQuery, setSearchQuery] = useState('');

    const reload = useCallback(async () => {
        try {
            const rows = await rigsApi.list({ limit: 2000 });
            setRigs(rows.map(rowToForm));
        } catch (e) {
            const msg = e instanceof ApiError ? e.message : 'Failed to load rigs.';
            setImportMessage({ text: msg, error: true });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void reload();
    }, [reload]);

    const filteredRigs = useMemo(() => {
        let list = rigs;
        if (filterSite !== 'all') list = list.filter((r) => r.site === filterSite);
        if (filterType !== 'all') list = list.filter((r) => r.rigType === filterType);
        if (filterStatus !== 'all') list = list.filter((r) => r.status === filterStatus);
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            list = list.filter(
                (r) =>
                    r.rigId.toLowerCase().includes(q) ||
                    r.manufacturer.toLowerCase().includes(q) ||
                    r.serialNumber.toLowerCase().includes(q)
            );
        }
        return list;
    }, [rigs, filterSite, filterType, filterStatus, searchQuery]);

    const sites = useMemo(
        () => [...new Set(rigs.map((r) => r.site))].filter(Boolean).sort(),
        [rigs]
    );

    if (!can('manage:org')) {
        return (
            <div className='max-w-2xl mx-auto py-12 text-center'>
                <p className='text-muted-foreground'>
                    You don't have permission to view this page.
                </p>
            </div>
        );
    }

    async function handleAdd() {
        if (!form.rigId) return;

        // Duplicate check is a client-side nicety. The backend also enforces
        // uniqueness via (org_id, rig_id) + ON CONFLICT upserts.
        if (!editingId && rigs.some((r) => r.rigId === form.rigId)) {
            setImportMessage({ text: `Rig ID "${form.rigId}" already exists.`, error: true });
            setTimeout(() => setImportMessage(null), 5000);
            return;
        }
        try {
            await rigsApi.upsert(formToPayload(form));
            await reload();
            setForm({ ...EMPTY_FORM });
            setEditingId(null);
        } catch (e) {
            const msg = e instanceof ApiError ? e.message : 'Save failed.';
            setImportMessage({ text: msg, error: true });
            setTimeout(() => setImportMessage(null), 5000);
        }
    }

    function startEdit(rig: RigForm) {
        setForm({ ...rig });
        setEditingId(rig.id ?? null);
    }

    function cancelEdit() {
        setForm({ ...EMPTY_FORM });
        setEditingId(null);
    }

    async function handleDelete(rig: RigForm) {
        if (!rig.id) return;
        try {
            await rigsApi.remove(rig.id);
            await reload();
            if (editingId === rig.id) cancelEdit();
        } catch (e) {
            const msg = e instanceof ApiError ? e.message : 'Delete failed.';
            setImportMessage({ text: msg, error: true });
            setTimeout(() => setImportMessage(null), 5000);
        }
    }

    async function handleBulkImport() {
        if (!selectedFile) return;
        const text = await selectedFile.text();
        const { entries, errors } = parseRigsCSV(text);
        setShowUpload(false);
        setSelectedFile(null);

        if (entries.length === 0) {
            setImportMessage({ text: errors.join(' | ') || 'No valid rigs found.', error: true });
            setTimeout(() => setImportMessage(null), 15000);
            return;
        }

        // POST each row — the backend upserts on (org_id, rig_id) so a
        // repeat import overwrites the existing record.
        let ok = 0;
        const failed: string[] = [];
        for (const e of entries) {
            try {
                await rigsApi.upsert(formToPayload(e));
                ok++;
            } catch (err) {
                failed.push(`${e.rigId}: ${err instanceof ApiError ? err.message : 'failed'}`);
            }
        }
        await reload();
        const msg =
            failed.length > 0
                ? `Imported ${ok} rig${ok === 1 ? '' : 's'}. ${failed.length} failed: ${failed[0]}`
                : `Imported ${ok} rig${ok === 1 ? '' : 's'} successfully.${errors.length > 0 ? ` ${errors.length} row(s) skipped.` : ''}`;
        setImportMessage({ text: msg, error: failed.length > 0 });
        setTimeout(() => setImportMessage(null), 15000);
    }

    return (
        <div className='max-w-6xl mx-auto'>
            <div className='mb-6'>
                <h1 className='text-2xl font-bold text-foreground'>Rig Register</h1>
                <p className='text-muted-foreground'>
                    Manage drill rigs across sites. Changes here sync to the operator app for
                    selection at shift start.
                </p>
            </div>

            {/* Add / Edit form */}
            <Card className='bg-card border-border mb-6'>
                <CardHeader className='pb-2'>
                    <CardTitle className='text-foreground text-base'>
                        {editingId ? `Edit ${form.rigId}` : 'Add Rig'}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className='grid grid-cols-2 md:grid-cols-4 gap-3 p-4 rounded-lg border border-border bg-background'>
                        <div className='space-y-1'>
                            <Label className='text-xs text-muted-foreground'>Rig ID</Label>
                            <Input
                                className='h-9 font-mono'
                                placeholder='e.g. DR1001'
                                value={form.rigId}
                                onChange={(e) => setForm((f) => ({ ...f, rigId: e.target.value }))}
                                disabled={!!editingId}
                            />
                        </div>

                        <div className='space-y-1'>
                            <Label className='text-xs text-muted-foreground'>Type</Label>
                            <Select
                                value={form.rigType}
                                onValueChange={(v) =>
                                    setForm((f) => ({ ...f, rigType: v as RigType }))
                                }
                            >
                                <SelectTrigger className='h-9'>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(TYPE_LABELS).map(([v, l]) => (
                                        <SelectItem key={v} value={v}>
                                            {l}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className='space-y-1'>
                            <Label className='text-xs text-muted-foreground'>Manufacturer</Label>
                            <Select
                                value={form.manufacturer}
                                onValueChange={(v) => setForm((f) => ({ ...f, manufacturer: v }))}
                            >
                                <SelectTrigger className='h-9'>
                                    <SelectValue placeholder='Select...' />
                                </SelectTrigger>
                                <SelectContent>
                                    {MANUFACTURERS.map((m) => (
                                        <SelectItem key={m} value={m}>
                                            {m}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className='space-y-1'>
                            <Label className='text-xs text-muted-foreground'>Serial Number</Label>
                            <Input
                                className='h-9 font-mono'
                                placeholder='e.g. EP-D65-001'
                                value={form.serialNumber}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, serialNumber: e.target.value }))
                                }
                            />
                        </div>

                        <div className='space-y-1'>
                            <Label className='text-xs text-muted-foreground'>Site</Label>
                            <Select
                                value={form.site}
                                onValueChange={(v) => setForm((f) => ({ ...f, site: v }))}
                            >
                                <SelectTrigger className='h-9'>
                                    <SelectValue placeholder='Select...' />
                                </SelectTrigger>
                                <SelectContent>
                                    {SITES.map((s) => (
                                        <SelectItem key={s} value={s}>
                                            {s}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className='space-y-1'>
                            <Label className='text-xs text-muted-foreground'>Status</Label>
                            <Select
                                value={form.status}
                                onValueChange={(v) =>
                                    setForm((f) => ({ ...f, status: v as RigStatus }))
                                }
                            >
                                <SelectTrigger className='h-9'>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(STATUS_LABELS).map(([v, l]) => (
                                        <SelectItem key={v} value={v}>
                                            {l}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className='space-y-1 md:col-span-2'>
                            <Label className='text-xs text-muted-foreground'>Notes</Label>
                            <Input
                                className='h-9'
                                placeholder='Optional notes...'
                                value={form.notes}
                                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                            />
                        </div>

                        <div className='flex items-end gap-2 md:col-span-4'>
                            <Button
                                size='sm'
                                onClick={() => void handleAdd()}
                                disabled={!form.rigId}
                            >
                                {editingId ? (
                                    <Pencil className='h-4 w-4 mr-2' />
                                ) : (
                                    <Plus className='h-4 w-4 mr-2' />
                                )}
                                {editingId ? 'Update' : 'Add Rig'}
                            </Button>
                            {editingId && (
                                <Button size='sm' variant='ghost' onClick={cancelEdit}>
                                    <X className='h-4 w-4 mr-1' /> Cancel
                                </Button>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Filters + actions bar */}
            <Card className='bg-card border-border'>
                <CardHeader>
                    <div className='flex items-start justify-between gap-4 flex-wrap'>
                        <div>
                            <CardTitle className='text-foreground flex items-center gap-2'>
                                <Truck className='h-5 w-5 text-primary' />
                                Registered Rigs
                                <span className='text-sm font-normal text-muted-foreground ml-2'>
                                    {filteredRigs.length} rig{filteredRigs.length !== 1 ? 's' : ''}
                                    {filteredRigs.length !== rigs.length && ` of ${rigs.length}`}
                                </span>
                            </CardTitle>
                            {importMessage && (
                                <p
                                    className={`text-xs mt-2 ${importMessage.error ? 'text-destructive' : 'text-green-500'}`}
                                >
                                    {importMessage.text}
                                </p>
                            )}
                        </div>
                        <div className='flex gap-2'>
                            <Button variant='outline' size='sm' onClick={() => exportRigsCSV(rigs)}>
                                <Download className='h-3.5 w-3.5 mr-1.5' /> Export CSV
                            </Button>
                            <Button variant='outline' size='sm' onClick={() => setShowUpload(true)}>
                                <Upload className='h-3.5 w-3.5 mr-1.5' /> Import CSV
                            </Button>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className='flex gap-3 flex-wrap mt-3'>
                        <div className='relative flex-1 min-w-[200px]'>
                            <Search className='absolute left-2.5 top-2 h-4 w-4 text-muted-foreground' />
                            <Input
                                className='h-8 pl-9 text-sm'
                                placeholder='Search rig ID, manufacturer, serial...'
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <Select value={filterSite} onValueChange={setFilterSite}>
                            <SelectTrigger className='h-8 w-[140px] text-sm'>
                                <SelectValue placeholder='All Sites' />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value='all'>All Sites</SelectItem>
                                {sites.map((s) => (
                                    <SelectItem key={s} value={s}>
                                        {s}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterType} onValueChange={setFilterType}>
                            <SelectTrigger className='h-8 w-[130px] text-sm'>
                                <SelectValue placeholder='All Types' />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value='all'>All Types</SelectItem>
                                {Object.entries(TYPE_LABELS).map(([v, l]) => (
                                    <SelectItem key={v} value={v}>
                                        {l}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterStatus} onValueChange={setFilterStatus}>
                            <SelectTrigger className='h-8 w-[140px] text-sm'>
                                <SelectValue placeholder='All Status' />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value='all'>All Status</SelectItem>
                                {Object.entries(STATUS_LABELS).map(([v, l]) => (
                                    <SelectItem key={v} value={v}>
                                        {l}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardHeader>

                <CardContent>
                    {loading ? (
                        <p className='text-sm text-muted-foreground text-center py-8'>
                            Loading rigs…
                        </p>
                    ) : filteredRigs.length > 0 ? (
                        <div className='overflow-x-auto'>
                            <table className='w-full text-sm'>
                                <thead>
                                    <tr className='border-b border-border text-muted-foreground text-left'>
                                        <th className='pb-2 pr-4 font-medium'>Rig ID</th>
                                        <th className='pb-2 pr-4 font-medium'>Type</th>
                                        <th className='pb-2 pr-4 font-medium'>Manufacturer</th>
                                        <th className='pb-2 pr-4 font-medium'>Serial</th>
                                        <th className='pb-2 pr-4 font-medium'>Site</th>
                                        <th className='pb-2 pr-4 font-medium'>Status</th>
                                        <th className='pb-2 pr-4 font-medium'>Notes</th>
                                        <th className='pb-2 font-medium' />
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRigs.map((r) => (
                                        <tr
                                            key={r.id ?? r.rigId}
                                            className='border-b border-border/50 hover:bg-muted/30'
                                        >
                                            <td className='py-2.5 pr-4 text-foreground font-mono font-medium'>
                                                {r.rigId}
                                            </td>
                                            <td className='py-2.5 pr-4 text-foreground'>
                                                {TYPE_LABELS[r.rigType]}
                                            </td>
                                            <td className='py-2.5 pr-4 text-foreground'>
                                                {r.manufacturer || '—'}
                                            </td>
                                            <td className='py-2.5 pr-4 text-foreground font-mono'>
                                                {r.serialNumber || '—'}
                                            </td>
                                            <td className='py-2.5 pr-4 text-foreground'>
                                                {r.site || '—'}
                                            </td>
                                            <td className='py-2.5 pr-4'>
                                                <Badge
                                                    variant='outline'
                                                    className={`text-xs ${STATUS_COLORS[r.status]}`}
                                                >
                                                    {STATUS_LABELS[r.status]}
                                                </Badge>
                                            </td>
                                            <td className='py-2.5 pr-4 text-muted-foreground text-xs max-w-[150px] truncate'>
                                                {r.notes || '—'}
                                            </td>
                                            <td className='py-2.5'>
                                                <div className='flex gap-1'>
                                                    <Button
                                                        variant='ghost'
                                                        size='icon'
                                                        className='h-7 w-7 text-muted-foreground hover:text-primary'
                                                        onClick={() => startEdit(r)}
                                                    >
                                                        <Pencil className='h-3.5 w-3.5' />
                                                    </Button>
                                                    <Button
                                                        variant='ghost'
                                                        size='icon'
                                                        className='h-7 w-7 text-muted-foreground hover:text-destructive'
                                                        onClick={() => void handleDelete(r)}
                                                    >
                                                        <Trash2 className='h-3.5 w-3.5' />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className='text-sm text-muted-foreground text-center py-8'>
                            {rigs.length === 0
                                ? 'No rigs registered yet. Add one above or import from CSV.'
                                : 'No rigs match the current filters.'}
                        </p>
                    )}
                </CardContent>
            </Card>

            {/* Bulk Upload Dialog */}
            <Dialog
                open={showUpload}
                onOpenChange={(open) => {
                    setShowUpload(open);
                    if (!open) setSelectedFile(null);
                }}
            >
                <DialogContent className='sm:max-w-md'>
                    <DialogHeader>
                        <DialogTitle className='flex items-center gap-2'>
                            <TableProperties className='h-5 w-5 text-primary' />
                            Import Rig Register
                        </DialogTitle>
                        <p className='text-sm text-muted-foreground'>
                            Upload a CSV to add or update rigs. Existing rigs with matching rig IDs
                            will be updated.
                        </p>
                    </DialogHeader>

                    <div className='flex items-center justify-between bg-muted/40 rounded-lg px-4 py-3'>
                        <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                            <Download className='h-4 w-4' />
                            Download template with sample data
                        </div>
                        <Button variant='outline' size='sm' onClick={downloadRigTemplate}>
                            <Download className='h-3.5 w-3.5 mr-1' /> Template
                        </Button>
                    </div>

                    <button
                        type='button'
                        onClick={() => fileInputRef.current?.click()}
                        className='w-full border-2 border-dashed border-border rounded-lg py-10 flex flex-col items-center gap-2 hover:border-primary/50 transition-colors'
                    >
                        <Upload className='h-8 w-8 text-muted-foreground' />
                        <p className='text-sm font-medium text-foreground'>
                            {selectedFile ? selectedFile.name : 'Click to upload CSV file'}
                        </p>
                        <p className='text-xs text-muted-foreground'>.csv files only</p>
                    </button>
                    <input
                        ref={fileInputRef}
                        type='file'
                        accept='.csv'
                        className='hidden'
                        onChange={(e) => {
                            setSelectedFile(e.target.files?.[0] ?? null);
                            e.target.value = '';
                        }}
                    />

                    <div className='flex justify-end gap-2 pt-1'>
                        <Button
                            variant='outline'
                            onClick={() => {
                                setShowUpload(false);
                                setSelectedFile(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button onClick={() => void handleBulkImport()} disabled={!selectedFile}>
                            <Upload className='h-4 w-4 mr-1.5' /> Import
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
